/* eslint-disable unicorn/prefer-module */
require('@mobile-reality/mdma-renderer-react/styles.css');
const React = require('react');
const Cookies = require('js-cookie');
const { PATHS } = require('constants/paths');
const { getPageContext } = require('constants/content-groups');
const { initClickTracking } = require('utilities/index');
const { RecaptchaWrapper } = require('components/recaptcha-wrapper');
const { ConvexClientProvider } = require('components/convex-provider');
const { ConfirmProvider } = require('components/confirm-dialog');

const pathsWithForms = [
    `${PATHS.ESTIMATE_THE_PROJECT}/`,
    `${PATHS.CONTACT_US}/`,
    `${PATHS.CAREERS}/`,
    `${PATHS.PRIVACY_POLICY}/`,
    `${PATHS.RESOURCES}/`,
    `${PATHS.CTO_AS_SERVICE}/`,
    `services/${PATHS.CTO_AS_SERVICE}/`,
    `services/${PATHS.CAIO_AS_SERVICE}/`,
    `services/${PATHS.AI_AUTOMATION_AGENCY}/`,
    `${PATHS.TECHNOLOGIES}/${PATHS.AI_AGENT}/`,
    `${PATHS.RESOURCES}/${PATHS.CROSS_PLATFORM_WHITE_PAPER}/`,
    `${PATHS.RESOURCES}/${PATHS.MEDICAL_DATA_PROTECTION}/`,
    `${PATHS.RESOURCES}/${PATHS.INTEGRATION_READINESS_CHECK}/`,
    `${PATHS.SOLUTIONS}/${PATHS.DISCOVERY_WORKSHOPS}/`,
    `${PATHS.SOLUTIONS}/${PATHS.AI_FOR_PROPERTY_MANAGEMENT}/`,
    `${PATHS.SOLUTIONS}/${PATHS.FORECASTING}/`,
];

const ConditionalRecaptchaProvider = ({ children, location }) => {
    const currentPath = location.pathname.startsWith('/')
        ? location.pathname.slice(1)
        : location.pathname;

    const shouldLoadRecaptcha = pathsWithForms.includes(currentPath);

    return shouldLoadRecaptcha ? (
        <RecaptchaWrapper>{children}</RecaptchaWrapper>
    ) : (
        children
    );
};

exports.wrapPageElement = ({ element, props }) => {
    return (
        <ConditionalRecaptchaProvider location={props.location}>
            {element}
        </ConditionalRecaptchaProvider>
    );
};

// Wrap the entire app with Convex provider
exports.wrapRootElement = ({ element }) => {
    return (
        <ConvexClientProvider>
            <ConfirmProvider>{element}</ConfirmProvider>
        </ConvexClientProvider>
    );
};

// Hydration mismatches (React #418/#423/#425) are recovered silently and the
// minified message never says which component diverged. Report the component
// stack so Clarity (custom tag + event) and GTM can pinpoint it. Production
// only — `gatsby develop` has no SSR HTML to hydrate and gives full errors.
const reportRecoverableError = (error, errorInfo) => {
    // Keep React's default behaviour: surfaces in the console and as a window
    // error, which is what Clarity's "JavaScript errors" already picks up.
    if (typeof window.reportError === 'function') {
        window.reportError(error);
    } else {
        console.error(error);
    }

    const componentStack = (errorInfo?.componentStack || '')
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean)
        .slice(0, 8)
        .join(' < ');

    // Only the React error code goes to analytics, never the raw message: a
    // recoverable error may carry user data or tokens in its text.
    const errorCode =
        String(error?.message || '').match(/React error #(\d+)/)?.[1] ||
        'unknown';

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
        event: 'react_recoverable_error',
        react_error_code: errorCode,
        react_component_stack: componentStack.slice(0, 500),
    });

    if (typeof window.clarity === 'function') {
        window.clarity('set', 'react_hydration_stack', componentStack.slice(0, 255));
        window.clarity('event', 'react_hydration_error');
    }
};

if (process.env.NODE_ENV === 'production') {
    exports.replaceHydrateFunction = () => (element, container) => {
        const { createRoot, hydrateRoot } = require('react-dom/client');

        // Same split as Gatsby's default renderer: hydrate prerendered HTML,
        // client-render an empty container (e.g. client-only 404 fallback).
        if (container.hasChildNodes()) {
            return hydrateRoot(container, element, {
                onRecoverableError: reportRecoverableError,
            });
        }

        const root = createRoot(container);
        root.render(element);
        return root;
    };
}

// Marks the browser as Mobile Reality's own: GTM sends traffic_type=internal
// with every GA4 event and GA4's "Internal Traffic" filter drops it. Set by a
// CMS (/auth) visit or by opening any page with ?internal=1; ?internal=0 clears it.
const INTERNAL_COOKIE = 'mr_internal';

const markInternalTraffic = ({ pathname, search }) => {
    const flag = new URLSearchParams(search).get('internal');
    if (flag === '0') {
        Cookies.remove(INTERNAL_COOKIE);
    } else if (flag === '1' ||
        pathname === '/auth' ||
        pathname.startsWith('/auth/')) {
        Cookies.set(INTERNAL_COOKIE, '1', { expires: 400, sameSite: 'lax' });
    }
};

// One `cta_click` per click on any link or button (see utilities/index.js).
exports.onClientEntry = () => {
    initClickTracking();
};

// Fires on the first render and on every client-side navigation. GTM builds
// the GA4 page_view (and any other per-page tag) on this event — nothing sends
// page views from code. content_group / content_language stay in GTM's data
// model, so every later event on the page carries them too.
exports.onRouteUpdate = ({ location }) => {
    if (window.CLUTCHCO !== undefined) {
        window.CLUTCHCO.Init();
    }
    markInternalTraffic(location);
    // Deferred so the new page's <title> is in place when GTM reads it.
    setTimeout(() => {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            event: 'gatsbyRouteChange',
            ...getPageContext(location.pathname),
        });
    }, 50);
};
